import { defineStore } from 'pinia';
import { useCurrencyConverter } from '~/composables/useCurrencyConverter';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { ELinkType } from '~/types/links';
import { EModalName } from '~/types/modals';
import {
  ENotificationSettingsType,
  ENotificationType,
  type INotificationCommon,
  type INotificationFeedElement,
} from '~/types/notifications';
import { NOTIFS_REQUEST_INTERVAL } from '~/utils/constants';
import { DAY_MILLISECONDS } from '~/utils/date/constants';
import { formatAmmount } from '~/utils/notifications/ammountFormating';
import { getCashOutStatus, getStatusTitle } from '~/utils/notifications/getCashOutStatus';
import {
  shouldShowBonusNotification,
  shouldShowCashOutNotification,
  shouldShowFreespinNotification,
} from '~/utils/notifications/notificationFilters';
import { useStoreNotificationsStatus } from '../client/notificationsStatus';
import { useStoreSsPlayerBonuses } from '../softswiss/playerBonuses';
import { useStoreSsPlayerPayments } from '../softswiss/playerPayments';
import { useStoreComponents } from '../strapi/components';
import { useStoreNotificationsGroup } from '../strapi/notificationsGroup';
import { useStoreNotificationsPersonal } from '../strapi/notificationsPersonal';
import { useStoreNotificationsSettings } from '../strapi/notificationsSettings';
import { useStoreNotificationsWelcome } from '../strapi/notificationsWelcome';
import { useStoreVipLevelData } from '../strapi/vipLevelData';
import { useStorePages } from './pages';
import type { IVipData } from '~/types/vip';

export const useStoreNotifications = defineStore('notifications', {
  state: () => ({
    requestTimeout: NaN,
    hideConfiramtionFrom: '',
    currentDate: Date.now(),
  }),

  getters: {
    /*
      Gets personal and group notifs and converts to united interface
      which will be used in general feed (see 'feed' getter)
    */
    feedNotifications() {
      const storePersonal = useStoreNotificationsPersonal();
      const storeGroup = useStoreNotificationsGroup();
      const storeComponents = useStoreComponents();
      const storeNotificationsStatus = useStoreNotificationsStatus();

      const result: INotificationFeedElement[] = [];

      if (!storeNotificationsStatus.isLoaded) {
        return result;
      }

      const notifications = [
        ...storePersonal.notificationsPersonal,
        ...storeGroup.notificationsGroup,
      ] as unknown as INotificationCommon[];

      const isReaded = (notification: INotificationCommon, isPersonal: boolean) => {
        const notificationType = isPersonal
          ? ENotificationType.notification_personal
          : ENotificationType.notification_group;

        const notificationID = `${notificationType}_${notification.id}`;
        const notifStatusReaded =
          storeNotificationsStatus.notificationStatuses[notificationID]?.readed;
        return !!notifStatusReaded;
      };

      notifications.forEach((notification) => {
        const isPersonal = !!notification.playerId;
        const actionKey = isPersonal ? 'personal' : 'group';
        const timestamp = new Date(notification?.createdAt || '').getTime();

        result.push({
          id: `${actionKey}_${notification.id}`,
          type: ENotificationSettingsType.Notification,
          readed: isReaded(notification, isPersonal),
          timestamp,
          action: {
            type: ELinkType.modal,
            modal: EModalName.notification,
            params: { [actionKey]: String(notification.id) },
          },
          data: {
            icon: notification.manager?.bannerComplex.desktopAssetFront || null,
            typeTitle: `${storeComponents.notifications.fromTitle} ${notification.manager?.name}`,
            title: notification.title,
            messageText: notification.messageText,
          },
        });
      });

      return result;
    },

    /*
      Gets deposit notifs and converts to united interface
      which will be used in general feed (see 'feed' getter)
    */
    feedDeposits() {
      const storeSsPlayerPayments = useStoreSsPlayerPayments();
      const storeSettings = useStoreNotificationsSettings();

      const settingsData = storeSettings.notificationsSettings.find(
        (s) => s.type === ENotificationSettingsType.DepositInfo
      );

      const result: INotificationFeedElement[] = [];

      storeSsPlayerPayments.deposits.forEach((data) => {
        const status = getCashOutStatus(data);

        if (!shouldShowCashOutNotification(status)) {
          return;
        }

        const timestamp = new Date(data.finished_at || data.created_at).getTime();
        const isOld = new Date().getTime() - timestamp > DAY_MILLISECONDS;

        result.push({
          id: `${ENotificationSettingsType.DepositInfo}_${data.id}`,
          type: ENotificationSettingsType.DepositInfo,
          readed: !!data.finished_at || isOld,
          timestamp,
          action: {
            type: ELinkType.modal,
            modal: EModalName.transaction_history,
            params: { [EModalName.transaction_history]: String(data.id) },
          },
          data: {
            icon: settingsData?.icon || null,
            typeTitle: settingsData?.typeTitle || '',
            title: formatAmmount(data.amount_cents, data.currency),
            status: getStatusTitle(status),
            statusId: status,
          },
        });
      });

      return result;
    },

    /*
      Gets cashouts notifs and converts to united interface
      which will be used in general feed (see 'feed' getter)
    */
    feedCashouts() {
      const storeSsPlayerPayments = useStoreSsPlayerPayments();
      const storeSettings = useStoreNotificationsSettings();

      const settingsData = storeSettings.notificationsSettings.find(
        (s) => s.type === ENotificationSettingsType.CashOut
      );

      const result: INotificationFeedElement[] = [];

      storeSsPlayerPayments.cashouts.forEach((data) => {
        const status = getCashOutStatus(data);

        if (!shouldShowCashOutNotification(status)) {
          return;
        }

        result.push({
          id: `${ENotificationSettingsType.CashOut}_${data.id}`,
          type: ENotificationSettingsType.CashOut,
          readed: !!data.finished_at,
          timestamp: new Date(data?.finished_at || data.created_at).getTime(),
          action: {
            type: ELinkType.modal,
            modal: EModalName.transaction_history,
            params: { [EModalName.transaction_history]: String(data.id) },
          },
          data: {
            icon: settingsData?.icon || null,
            typeTitle: settingsData?.typeTitle || '',
            title: formatAmmount(data.amount_cents, data.currency),
            status: getStatusTitle(status),
            statusId: status,
          },
        });
      });

      return result;
    },

    /*
      Gets Bonuses notifs and converts to united interface
      which will be used in general feed (see 'feed' getter)
    */
    feedBonuses() {
      const storeSsPlayerBonuses = useStoreSsPlayerBonuses();
      const storeSettings = useStoreNotificationsSettings();
      const { convertToFormattedAmount } = useCurrencyConverter();

      const settingsData = storeSettings.notificationsSettings.find(
        (s) => s.type === ENotificationSettingsType.Bonuses
      );

      const result: INotificationFeedElement[] = [];

      const cashBonuses = storeSsPlayerBonuses.bonuses.filter(
        (data) => data.strategy !== 'exchange'
      );

      cashBonuses.forEach((data) => {
        const formattedTitleAmount = convertToFormattedAmount(data.amount_cents, data.currency);

        result.push({
          id: `${ENotificationSettingsType.Bonuses}_${data.id}`,
          type: ENotificationSettingsType.Bonuses,
          readed: !shouldShowBonusNotification(data),
          timestamp: new Date(data.created_at).getTime(),
          action: {
            type: ELinkType.modal,
            modal: EModalName.bonuses,
            params: { [EModalName.bonuses]: String(data.id) },
          },
          data: {
            banner: settingsData?.bannerComplex,
            icon: settingsData?.icon || null,
            typeTitle: settingsData?.typeTitle || '',
            title: `${formattedTitleAmount.formattedAmount} ${formattedTitleAmount.formattedCurrency}`,
            messageText: data.title,
          },
        });
      });

      return result;
    },

    /*
      Gets Bonuses notifs and converts to united interface
      which will be used in general feed (see 'feed' getter)
    */
    feedCompoints() {
      const storeSsPlayerBonuses = useStoreSsPlayerBonuses();
      const storeSettings = useStoreNotificationsSettings();
      const { convertToFormattedAmount } = useCurrencyConverter();

      const settingsData = storeSettings.notificationsSettings.find(
        (s) => s.type === ENotificationSettingsType.Compoints
      );

      const result: INotificationFeedElement[] = [];

      const compointsData = storeSsPlayerBonuses.bonuses.filter(
        (data) => data.strategy === 'exchange'
      );

      compointsData.forEach((data) => {
        const formattedTitleAmount = convertToFormattedAmount(data.amount_cents, data.currency);

        result.push({
          id: `${ENotificationSettingsType.Compoints}_${data.id}`,
          type: ENotificationSettingsType.Compoints,
          readed: !shouldShowBonusNotification(data),
          timestamp: new Date(data.created_at).getTime(),
          action: {
            type: ELinkType.modal,
            modal: EModalName.bonuses,
            params: { [EModalName.bonuses]: String(data.id) },
          },
          data: {
            banner: settingsData?.bannerComplex,
            icon: settingsData?.icon || null,
            typeTitle: settingsData?.typeTitle || '',
            title: `${formattedTitleAmount.formattedAmount} ${formattedTitleAmount.formattedCurrency}`,
            messageText: data.title,
          },
        });
      });

      return result;
    },

    /*
      Gets freespin notifs and converts to united interface
      which will be used in general feed (see 'feed' getter)
    */
    feedFreespins() {
      const storeSsPlayerBonuses = useStoreSsPlayerBonuses();
      const storeSettings = useStoreNotificationsSettings();
      const storeComponents = useStoreComponents();

      const settingsData = storeSettings.notificationsSettings.find(
        (s) => s.type === ENotificationSettingsType.Freespins
      );

      const result: INotificationFeedElement[] = [];

      storeSsPlayerBonuses.freespins.forEach((data) => {
        result.push({
          id: `${ENotificationSettingsType.Freespins}_${data.id}`,
          type: ENotificationSettingsType.Freespins,
          readed: !shouldShowFreespinNotification(data),
          timestamp: new Date(data.created_at).getTime(),
          action: {
            type: ELinkType.modal,
            modal: EModalName.freespins,
            params: { [EModalName.freespins]: String(data.id) },
          },
          data: {
            icon: settingsData?.icon || null,
            typeTitle: settingsData?.typeTitle || '',
            title: `${data.freespins_total} ${storeComponents.placeholders.freespins}`,
            messageText: data.title,
          },
        });
      });

      return result;
    },

    /*
      checks if user has unread levelup notif/modal and add get it
      to be used in general feed (see 'feed' getter)
    */
    feedLevelup(state): INotificationFeedElement[] {
      const storeVipLevelData = useStoreVipLevelData();
      const storeSettings = useStoreNotificationsSettings();
      const timestamp = state.currentDate;
      const storeSsPlayer = useStoreSsPlayer();

      if (!storeSsPlayer.isPlayerAuthorised || !storeVipLevelData.isVipGroup) {
        return [];
      }

      const settingsData = storeSettings.notificationsSettings.find(
        (s) => s.type === ENotificationSettingsType.LevelUp
      );

      const result: INotificationFeedElement[] = [];

      const isReaded = (item: IVipData) => {
        if (storeVipLevelData.isVipSeenGroup && item.level <= storeVipLevelData.vipSeenGroupLevel) {
          return true;
        }

        if (!storeVipLevelData.isVipSeenGroup && item.level < storeVipLevelData.vipGroupLevel) {
          return true;
        }

        return false;
      };

      storeVipLevelData.data.forEach((item) => {
        if (item.level <= storeVipLevelData.vipGroupLevel) {
          result.push({
            id: 'levelup_' + item.level,
            type: ENotificationSettingsType.LevelUp,
            readed: isReaded(item),
            timestamp,
            action: {
              type: ELinkType.modal,
              modal: EModalName.level_up,
              params: {},
            },
            data: {
              icon: settingsData?.icon || null,
              typeTitle: settingsData?.typeTitle || '',
              title: item.title || '',
              messageText: '',
              vipMessageText: item.messageText,
              banner: { desktopAssetFront: item.levelImage },
            },
          });
        }
      });

      return result;
    },

    /*
      checks if user has unread welcome notif/modal and add get it
      to be used in general feed (see 'feed' getter)
    */
    feedWelcome() {
      const storeNotificationsStatus = useStoreNotificationsStatus();
      const storeWelcome = useStoreNotificationsWelcome();
      const storeSettings = useStoreNotificationsSettings();

      const settingsData = storeSettings.notificationsSettings.find(
        (s) => s.type === ENotificationSettingsType.Welcome
      );

      const result: INotificationFeedElement[] = [];

      if (!storeNotificationsStatus.isLoaded) {
        return result;
      }

      storeWelcome.notificationsWelcome.forEach((data) => {
        const notificationID = `${EModalName.welcome}_${data.id}`;

        const isReaded = !!storeNotificationsStatus.notificationStatuses[notificationID]?.readed;
        const timestamp = new Date(data?.createdAt || '').getTime();

        result.push({
          id: `${ENotificationSettingsType.Welcome}_${data.id}`,
          type: ENotificationSettingsType.Welcome,
          readed: isReaded,
          timestamp,
          action: {
            type: ELinkType.modal,
            modal: EModalName.welcome,
            params: { [EModalName.welcome]: String(data.id) },
          },
          data: {
            icon: data.icon || settingsData?.icon || null,
            typeTitle: data.typeTitle,
            title: data.title,
            messageText: data.messageText,
          },
        });
      });

      return result;
    },

    /*
      checks if user has unread confirm email notif/modal and add get it
      to be used in general feed (see 'feed' getter)
    */
    feedConfirmEmail(): INotificationFeedElement[] {
      const storeSsPlayer = useStoreSsPlayer();
      const storeSettings = useStoreNotificationsSettings();
      const storeComponents = useStoreComponents();

      const settingsData = storeSettings.notificationsSettings.find(
        (s) => s.type === ENotificationSettingsType.ConfirmEmail
      );

      if (storeSsPlayer.isPlayerAuthorised && !storeSsPlayer.playerInfo.confirmed_at) {
        return [
          {
            id: ENotificationSettingsType.ConfirmEmail,
            type: ENotificationSettingsType.ConfirmEmail,
            readed: false,
            timestamp: this.currentDate,
            action: {
              type: ELinkType.modal,
              modal: EModalName.confirm,
              params: {},
            },
            data: {
              icon: settingsData?.icon || null,
              typeTitle: settingsData?.typeTitle || '',
              title: storeComponents.notifications.verifyEmailTitle,
            },
          },
        ];
      }
      return [];
    },

    /*
      returns all kinds of notifs converted to single interface
    */
    feed(): INotificationFeedElement[] {
      return [
        ...this.feedNotifications,
        ...this.feedDeposits,
        ...this.feedCashouts,
        ...this.feedBonuses,
        ...this.feedCompoints,
        ...this.feedFreespins,
        ...this.feedLevelup,
        ...this.feedWelcome,
        ...this.feedConfirmEmail,
      ].sort((a, b) => b.timestamp - a.timestamp);
    },

    /* filter for field readed = false */
    feedUnreaded(): INotificationFeedElement[] {
      return this.feed.filter((n) => !n.readed);
    },

    /* filter for field readed = true */
    feedReaded(): INotificationFeedElement[] {
      return this.feed.filter((n) => n.readed);
    },
  },

  actions: {
    /**
     * function get notifications
     **/
    subscribe() {
      if (!import.meta.browser) {
        console.error('SSR BLOCK! action notifications/subscribe can be call only from browser!');
        return false;
      }

      if (!this.requestTimeout) {
        this.requestsByTimeout();
      }
    },

    requestsByTimeout() {
      if (!import.meta.browser || !window) {
        return;
      }

      clearTimeout(this.requestTimeout);

      if (useStoreSsPlayer().isPlayerAuthorised) {
        useStorePages()
          .loadNotifications()
          .then(() => {
            useStoreNotificationsStatus().load({
              playerId: useStoreSsPlayer().playerId,
            });
            useStoreNotificationsPersonal().loadNotificationsPersonal();
          });
      } else {
        console.warn('not authorised for request of notifications!');
      }

      this.requestTimeout = window.setTimeout(() => {
        this.requestsByTimeout();
      }, NOTIFS_REQUEST_INTERVAL);
    },

    unsubscribe() {
      if (!import.meta.browser) {
        console.error('SSR BLOCK! action notifications/subscribe can be call only from browser!');
        return false;
      }

      useStoreNotificationsGroup().clearStore();
      useStoreNotificationsPersonal().clearStore();
      useStoreNotificationsWelcome().clearStore();
      useStoreNotificationsStatus().clearStore();
      useStoreVipLevelData().hideNotification();

      if (this.requestTimeout) {
        clearTimeout(this.requestTimeout);
        this.requestTimeout = NaN;
      }
    },
  },
});
